import { deepMix } from '@antv/util';
import noop from 'utils/noop';
import { ItemState } from '../../../constants';
import { deleteUtils } from '../../../shapes/utils';
import { CustomNode, DrawOpts, Group, Item, NodeModel } from '../../../types';
import { getModelStateDataValue, shouldItemShowState } from '../../../utils';
import { NODE_TYPES } from '../../constants';
import { noteStateStyles, noteStyles } from './note.styles';
import noteUtils from './utils';

export const noteNodeConfig: Omit<CustomNode, 'getSize'> = {
  options: {
    center: 'bottomRight',
    defaultTitle: '',
    ...noteStyles,
    stateStyles: noteStateStyles,
  },
  shapeType: NODE_TYPES.NOTE,
  getCustomConfig() {
    return {};
  },
  getSize(model: NodeModel) {
    return noteUtils.parseSize(this.getOptions(model));
  },
  /**
   * TODO: As this functionality is used in pretty much all of our nodes, we
   * should create a base node that these all inherit the functionality from
   */
  parseItemStateStyles(model: NodeModel, item: Item) {
    const options = this.getOptions(model);
    const states = item.getStates();
    const stateOptions = noteUtils
      .getStatePriorities()
      .reduce((acc: DrawOpts['options'][], { state, onChange = noop }) => {
        const hasState = states.includes(state);
        const dataValue = getModelStateDataValue(model, state);
        const isDataSet = typeof dataValue === 'boolean';
        const shouldHaveState = isDataSet ? dataValue : hasState;

        if (isDataSet && shouldHaveState !== hasState) {
          onChange(item, shouldHaveState);
        }

        return shouldHaveState ? [...acc, this.getStateStyle(state, item)] : acc;
      }, []);

    return deepMix({}, options, ...stateOptions);
  },
  draw(model: NodeModel, group: Group) {
    const options = this.getOptions(model);
    const size = noteUtils.parseSize(options);
    const keyShape = noteUtils.drawSticky({ group, size, model, options });

    noteUtils.drawTitle({ group, size, model, options });
    noteUtils.drawBody({ group, size, model, options });
    noteUtils.drawLinkIcon({ group, size, model, options });

    if (deleteUtils.isEnabled(model)) {
      deleteUtils.draw({ size, options, group, model });
    }

    if (model?.isHidden) {
      group.hide();
    }

    return keyShape;
  },
  update(model: NodeModel, item: Item) {
    const group = item.getContainer();
    const isCurrentlyVisible = item.isVisible();
    const shouldBeVisible = !model?.isHidden;

    // Toggle visibility
    if (isCurrentlyVisible !== shouldBeVisible) {
      item.changeVisibility(shouldBeVisible);
    }

    // don't bother with other calculations if the visibility is hidden.
    if (!shouldBeVisible) return;

    const options = this.parseItemStateStyles(model, item);

    const isInlineEditInProgress = shouldItemShowState(item, ItemState.InlineEdit);
    const isHoverInProgress = shouldItemShowState(item, ItemState.Active);
    const baseNoteSize = noteUtils.parseSize(options);
    // The note expands to fit the content when hovered over, so we need to
    // calculate the size dynamically instead of using the fixed size.
    const size = isHoverInProgress
      ? noteUtils.getExpandedNoteSize({ options, size: baseNoteSize, model })
      : baseNoteSize;

    noteUtils.updateSticky({ group, size, model, options, item });
    noteUtils.updateTitle({ group, size, model, options });
    noteUtils.updateBody({ group, size, model, options });
    noteUtils.updateLinkIcon({ group, size, model, options });

    if (deleteUtils.isEnabled(model)) {
      deleteUtils.updatePosition({ size, options, group, model });
      deleteUtils.updateVisibility({ group, isVisible: !isInlineEditInProgress });
    }
  },
};
